/* eslint-disable no-console -- Intentional CWV debug logging to make telemetry sends visible during troubleshooting */

import type {NextWebVitalsMetric} from 'next/app'
import {
	getLocale,
	getSite,
	getTemplate,
} from '@/lib/pageContext/page-context-store'
import {env} from '@/services/env/client'
import {trackEvent} from '@/services/logger/client'
import {getTrustArcConsent, type ConsentLevel} from '@/services/trustarc'

type MetricRating = 'good' | 'needs-improvement' | 'poor'

type MetricName = 'LCP' | 'CLS' | 'INP' | 'FCP' | 'TTFB'

const WEB_VITALS_EVENT_NAME = 'web_vitals'
const PRODUCTION_APP_INSIGHTS_SAMPLE_RATE = 0.15
const PRODUCTION_GTM_SAMPLE_RATE = 0.05
const CLS_DECIMAL_PLACES = 3
const CWV_LOG_PREFIX = '[CWV Telemetry]'

const ALLOWED_METRICS = new Set<MetricName>([
	'LCP',
	'CLS',
	'INP',
	'FCP',
	'TTFB',
])

const ALLOWED_CONSENTS = new Set<ConsentLevel>([
	'ALL_COOKIES',
	'FUNCTIONAL_COOKIES',
])

const sample = (rate: number): boolean => {
	if (!Number.isFinite(rate) || rate <= 0) return false
	if (rate >= 1) return true
	return Math.random() < rate
}

const getConfiguredSampleRate = (
	configuredRate: unknown,
	fallbackRate: number
): number => {
	if (
		typeof configuredRate !== 'number' ||
		!Number.isFinite(configuredRate)
	) {
		return fallbackRate
	}

	return Math.min(Math.max(configuredRate, 0), 1)
}

const getAppInsightsSampleRate = (): number => {
	return getConfiguredSampleRate(
		env.NEXT_PUBLIC_CWV_APP_INSIGHTS_SAMPLE_RATE,
		PRODUCTION_APP_INSIGHTS_SAMPLE_RATE
	)
}

const getGtmSampleRate = (): number => {
	return getConfiguredSampleRate(
		env.NEXT_PUBLIC_CWV_GTM_SAMPLE_RATE,
		PRODUCTION_GTM_SAMPLE_RATE
	)
}

const roundToDecimalPlaces = (value: number, decimalPlaces: number): number => {
	const factor = 10 ** decimalPlaces
	return Math.round((value + Number.EPSILON) * factor) / factor
}

const normalizeMetricValue = (
	metricName: MetricName,
	metricValue: number
): number => {
	if (metricName === 'CLS') {
		return roundToDecimalPlaces(metricValue, CLS_DECIMAL_PLACES)
	}

	return roundToDecimalPlaces(metricValue, 0)
}

const getMetricRating = (metric: NextWebVitalsMetric): MetricRating => {
	switch (metric.name) {
		case 'LCP':
			if (metric.value <= 2500) return 'good'
			if (metric.value <= 4000) return 'needs-improvement'
			return 'poor'
		case 'CLS':
			if (metric.value <= 0.1) return 'good'
			if (metric.value <= 0.25) return 'needs-improvement'
			return 'poor'
		case 'INP':
			if (metric.value <= 200) return 'good'
			if (metric.value <= 500) return 'needs-improvement'
			return 'poor'
		case 'FCP':
			if (metric.value <= 1800) return 'good'
			if (metric.value <= 3000) return 'needs-improvement'
			return 'poor'
		case 'TTFB':
			if (metric.value <= 800) return 'good'
			if (metric.value <= 1800) return 'needs-improvement'
			return 'poor'
		default:
			return 'needs-improvement'
	}
}

const hasContext = (
	template: string,
	site: string,
	locale: string
): boolean => {
	return (
		Boolean(template && site && locale) &&
		template !== 'unknown' &&
		site !== 'unknown' &&
		locale !== 'unknown'
	)
}

const getErrorDetails = (error: unknown): Record<string, unknown> => {
	if (error instanceof Error) {
		return {
			error_name: error.name,
			error_message: error.message,
		}
	}

	if (typeof error === 'string') {
		return {
			error_message: error,
		}
	}

	if (typeof error === 'object' && error !== null) {
		try {
			return {
				error_message: JSON.stringify(error),
			}
		} catch {
			return {
				error_message: 'Unable to serialize thrown object',
			}
		}
	}

	return {
		error_message: String(error),
	}
}

const logCwvTelemetry = (
	message: string,
	details: Record<string, unknown> | (() => Record<string, unknown>)
): void => {
	if (env.NEXT_PUBLIC_CWV_DEBUG_LOGGING !== true) return

	console.log(
		`${CWV_LOG_PREFIX} ${message}`,
		typeof details === 'function' ? details() : details
	)
}

export function reportWebVitalsMetric(metric: NextWebVitalsMetric): void {
	try {
		if (typeof window === 'undefined') return
		const isDebugLoggingEnabled = env.NEXT_PUBLIC_CWV_DEBUG_LOGGING === true

		const isAllowedMetric = ALLOWED_METRICS.has(metric.name as MetricName)
		if (!isAllowedMetric) {
			logCwvTelemetry('Skipped unsupported metric', () => ({
				metric_name: metric.name,
				metric_id: metric.id,
				metric_value: metric.value,
			}))
			return
		}

		// CWV telemetry is only sent after consent so analytics respects user preference.
		const consentLevel = getTrustArcConsent()
		const hasConsent = ALLOWED_CONSENTS.has(consentLevel)
		const appInsightsSampleRate = getAppInsightsSampleRate()
		const gtmSampleRate = getGtmSampleRate()

		const appInsightsSampledIn = sample(appInsightsSampleRate)
		const gtmSampledIn = sample(gtmSampleRate)

		const appInsightsWouldSend = hasConsent && appInsightsSampledIn
		const gtmWouldSend = hasConsent && gtmSampledIn

		logCwvTelemetry('Metric received', () => ({
			metric_name: metric.name,
			metric_id: metric.id,
			metric_value: metric.value,
			consent_level: consentLevel,
			has_consent: hasConsent,
			sample_rates: {
				app_insights: appInsightsSampleRate,
				gtm: gtmSampleRate,
			},
			sampled_in: {
				app_insights: appInsightsSampledIn,
				gtm: gtmSampledIn,
			},
			will_send: {
				app_insights: appInsightsWouldSend,
				gtm: gtmWouldSend,
			},
		}))

		if (!hasConsent) {
			logCwvTelemetry(
				'Skipped metric because consent does not allow telemetry',
				() => ({
					metric_name: metric.name,
					metric_id: metric.id,
					consent_level: consentLevel,
					sample_rates: {
						app_insights: appInsightsSampleRate,
						gtm: gtmSampleRate,
					},
				})
			)
			return
		}

		const template = getTemplate()
		const site = getSite()
		const locale = getLocale()
		const pagePath = window.location.pathname

		if (!hasContext(template, site, locale) || !pagePath) {
			logCwvTelemetry(
				'Skipped metric because page context is incomplete',
				() => ({
					metric_name: metric.name,
					metric_id: metric.id,
					template,
					site,
					locale,
					page_path: pagePath,
				})
			)
			return
		}

		const metricName = metric.name
		const metricRating = getMetricRating(metric)
		const rawMetricValue = Number(metric.value)
		if (!Number.isFinite(rawMetricValue)) {
			logCwvTelemetry(
				'Skipped metric because value is not finite',
				() => ({
					metric_name: metric.name,
					metric_id: metric.id,
					metric_value: metric.value,
				})
			)
			return
		}

		const metricValue = normalizeMetricValue(
			metricName as MetricName,
			rawMetricValue
		)

		const properties = {
			metric_name: metricName,
			metric_rating: metricRating,
			metric_id: metric.id,
			template,
			site,
			locale,
			page_path: pagePath,
			consent_level: consentLevel,
		}

		// Sampling keeps telemetry volume and ingestion cost under control while preserving trends.
		if (appInsightsWouldSend) {
			// metric_value must be a measurement so App Insights stores it in customMeasurements.
			trackEvent({name: WEB_VITALS_EVENT_NAME}, properties, {
				metric_value: metricValue,
			})
		}

		if (isDebugLoggingEnabled) {
			logCwvTelemetry('App Insights dispatch decision', () => ({
				metric_name: metricName,
				metric_id: metric.id,
				sample_rate: appInsightsSampleRate,
				sampled_in: appInsightsSampledIn,
				would_send: appInsightsWouldSend,
				payload: appInsightsWouldSend
					? {
							name: WEB_VITALS_EVENT_NAME,
							properties,
							measurements: {
								metric_value: metricValue,
							},
						}
					: null,
			}))
		}

		const gtmAvailable = Array.isArray(window.dataLayer)
		let gtmPayload: Record<string, unknown> | null = null
		if (gtmWouldSend) {
			gtmPayload = {
				event: WEB_VITALS_EVENT_NAME,
				...properties,
				metric_value: metricValue,
			}
			window.dataLayer?.push(gtmPayload)
		}

		if (isDebugLoggingEnabled) {
			logCwvTelemetry('GTM dataLayer push result', () => ({
				metric_name: metricName,
				metric_id: metric.id,
				sample_rate: gtmSampleRate,
				sampled_in: gtmSampledIn,
				data_layer_available: gtmAvailable,
				pushed_to_data_layer: gtmWouldSend && gtmAvailable,
				payload: gtmPayload,
			}))
		}
	} catch (error) {
		// Never let telemetry failures affect user-facing execution.
		logCwvTelemetry('Telemetry handler threw and was suppressed', () => ({
			metric_name: metric.name,
			metric_id: metric.id,
			...getErrorDetails(error),
		}))
	}
}
